/**
 * =============================================================================
 * CheckoutErrorBanner — Banner premium para erros no checkout público
 * RELEASE: 2026-06-20 v1.0.0 — Substitui mensagens cruas do SDK (ex.: "Edge
 *                              Function returned a non-2xx status code") por
 *                              UI comercial: ícone, mensagem amigável e
 *                              "Código de referência" copiável para suporte.
 * =============================================================================
 */
import { useState } from 'react';
import { AlertCircle, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckoutErrorBannerProps {
  /** Mensagem amigável em PT-BR (vinda do envelope ou fallback). */
  message: string;
  /** Código de erro normalizado, usado para variações visuais opcionais. */
  code?: string | null;
  /** Identificador da tentativa (ex.: "att_5f8c1a") para correlação no suporte. */
  supportId?: string | null;
  className?: string;
}

/**
 * Exibe banner de erro premium. Cores e ícones via design tokens do projeto.
 */
export function CheckoutErrorBanner({
  message,
  code,
  supportId,
  className,
}: CheckoutErrorBannerProps) {
  const [copied, setCopied] = useState(false);

  // Copia o support_id para o clipboard (com fallback silencioso).
  const handleCopy = async () => {
    if (!supportId) return;
    try {
      await navigator.clipboard.writeText(supportId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Browsers sem permissão de clipboard — silencia.
    }
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3',
        'flex flex-col gap-1.5 text-sm',
        className,
      )}
      data-error-code={code ?? undefined}
    >
      <div className="flex items-start gap-2.5 text-destructive">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <p className="font-medium text-foreground leading-snug">{message}</p>
      </div>
      {supportId && (
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            'self-start ml-6 inline-flex items-center gap-1.5',
            'text-[11px] uppercase tracking-wide text-muted-foreground',
            'hover:text-foreground transition-colors',
          )}
          title="Copiar código de referência"
        >
          <span>Ref: {supportId}</span>
          {copied ? (
            <Check className="w-3 h-3 text-emerald-600" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
        </button>
      )}
    </div>
  );
}
